import React, { useState, useEffect } from "react";
import { useApp, Customer } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import CustomerDetails from '@/components/CustomerDetails';
import { Phone, QrCode, Search, Users, Loader2, Camera, X, User, ClipboardCheck } from "lucide-react";
import { formatPhoneNumber } from "@/lib/utils";
import toast from "react-hot-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { supabase } from "@/lib/supabase";
import { Html5QrcodeScanner } from "html5-qrcode";
import { useStaffNotifications } from "@/contexts/StaffNotificationContext";

const StaffDashboard: React.FC = () => {
  const { 
    addCustomerVisit, 
    getCustomerById, 
    getCustomerByPhone, 
    addCustomerByPhone,
    loadInitialData 
  } = useApp();
  const { addNotification } = useStaffNotifications();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [activeCustomerId, setActiveCustomerId] = useState<string>("");
  const [activeCustomerName, setActiveCustomerName] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [scanner, setScanner] = useState<Html5QrcodeScanner | null>(null);
  const [activeTab, setActiveTab] = useState("phone");
  
  // Function to handle modal open/close
  const handleModalOpenChange = (open: boolean) => {
    setShowScanModal(open);
    if (!open) {
      setActiveCustomerId("");
      setActiveCustomerName("");
    }
  };
  
  const handlePhoneLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneNumber.trim()) {
      toast.error("Please enter a phone number");
      return;
    }
    
    setIsSubmitting(true);
    try {
      const customerId = await addCustomerByPhone(phoneNumber);
      
      if (customerId && customerId.length > 0) {
        console.log("[StaffDashboard] Customer ID returned:", customerId);
        setActiveCustomerId(customerId);
        toast.success(getCustomerById(customerId) ? "Customer found in system!" : "New customer added successfully!");
      } else {
        console.error("[StaffDashboard] No customer ID returned");
        toast.error("Failed to find or create customer");
      }
    } catch (error) {
      console.error('[StaffDashboard] Error looking up customer:', error);
      toast.error('Failed to look up customer');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Initialize and clean up QR scanner when showQrScanner changes
  useEffect(() => {
    if (showQrScanner) {
      // Initialize QR scanner
      const qrScanner = new Html5QrcodeScanner(
        "staff-qr-scanner",
        { fps: 10, qrbox: { width: 250, height: 250 } },
        false
      );
      
      setScanner(qrScanner);
      
      // Start scanning
      qrScanner.render(handleScan, (error) => {
        console.error("[StaffDashboard] QR Scan error:", error);
      });
    } else {
      // Clean up scanner when not showing
      if (scanner) {
        scanner.clear().catch(error => {
          console.error("[StaffDashboard] Error clearing QR scanner:", error);
        });
        setScanner(null);
      }
    }
    
    // Cleanup on unmount or when showQrScanner changes
    return () => {
      if (scanner) {
        scanner.clear().catch(error => {
          console.error("[StaffDashboard] Error clearing QR scanner on cleanup:", error);
        });
      }
    };
  }, [showQrScanner]);

  // Handle QR code scan
  const handleScan = async (decodedText: string) => {
    try {
      console.log("[StaffDashboard] Processing QR code:", decodedText);
      
      // Extract customer ID from QR code (assuming format "dinetrack:uuid")
      if (decodedText.startsWith("dinetrack:")) {
        const customerId = decodedText.split("dinetrack:")[1];
        if (!customerId) {
          console.error("[StaffDashboard] Invalid QR code format - missing ID");
          toast.error("Invalid QR code format");
          return;
        }
        
        console.log("[StaffDashboard] Extracted customer ID:", customerId);
        
        // Get customer details from database
        const { data: customerData, error } = await supabase
          .from('customers')
          .select('*')
          .eq('id', customerId)
          .single();
          
        if (error || !customerData) {
          console.error("[StaffDashboard] No customer found for ID:", customerId);
          toast.error("Customer not found");
          return;
        }
        
        // Create a customer object
        const customer: Customer = {
          id: customerData.id,
          phoneNumber: customerData.phone_number,
          name: customerData.name,
          email: customerData.email,
          visits: [],
          redeemedRewards: []
        };
        
        // Stop scanner
        if (scanner) {
          await scanner.clear();
        }
        
        // Hide scanner
        setShowQrScanner(false);
        
        // Set customer data and show modal
        setActiveCustomerId(customerId);
        setActiveCustomerName(customer.name || formatPhoneNumber(customer.phoneNumber));
        setShowScanModal(true);
        
        // Also create a notification in the database for other staff members
        try {
          await supabase.from('staff_notifications').insert({
            type: 'scan',
            message: `Customer ${customer.name || formatPhoneNumber(customer.phoneNumber)} has scanned their QR code`,
            customer_id: customerId,
            created_at: new Date().toISOString()
          });
        } catch (notificationError) {
          console.error("[StaffDashboard] Error creating notification:", notificationError);
          // Continue anyway since we already have the customer data
        }
      } else {
        toast.error("Invalid QR code format");
      }
    } catch (error) {
      console.error("[StaffDashboard] Error processing QR code:", error);
      toast.error("Failed to process QR code");
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border-t-4 border-t-restaurant-primary shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center text-lg sm:text-xl">
            <User className="w-5 h-5 mr-2 text-restaurant-primary" />
            Customer Check-In
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="mb-4 bg-gray-100 w-full justify-start">
              <TabsTrigger
                value="phone"
                className="flex-1 sm:flex-none data-[state=active]:bg-white data-[state=active]:text-restaurant-primary"
              >
                <Phone className="w-4 h-4 mr-2" />
                Phone Lookup
              </TabsTrigger>
              <TabsTrigger
                value="qr"
                className="flex-1 sm:flex-none data-[state=active]:bg-white data-[state=active]:text-restaurant-primary"
              >
                <QrCode className="w-4 h-4 mr-2" />
                QR Scan
              </TabsTrigger>
            </TabsList>

            <TabsContent value="phone">
              <div className="mb-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Enter a customer's phone number to find their profile or add them to the system
                </p>
              </div>
              <form onSubmit={handlePhoneLookup} className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    type="tel"
                    placeholder="Enter phone number"
                    value={phoneNumber}
                    onChange={(e) => {
                      // Only allow numbers
                      const value = e.target.value.replace(/\D/g, "");
                      // Limit to 10 digits
                      setPhoneNumber(value.slice(0, 10));
                    }}
                    maxLength={10}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="border-restaurant-border focus-visible:ring-restaurant-primary"
                  />
                  <Button 
                    type="submit" 
                    disabled={isSubmitting || phoneNumber.length < 10}
                    className="bg-restaurant-primary hover:bg-restaurant-primary-hover"
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4" />
                    )}
                    Look Up
                  </Button>
                </div>
                <div className="text-xs text-muted-foreground">
                  {phoneNumber.length > 0 ? 
                    `${phoneNumber.length}/10 digits entered` : 
                    "Please enter a 10-digit phone number"}
                </div>
              </form>
            </TabsContent>
                
            <TabsContent value="qr">
              <div className="mb-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Scan a customer's QR code directly or enter their ID manually
                </p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                {!showQrScanner ? (
                  <div className="flex flex-col items-center gap-4 py-6 sm:py-8">
                    <Button 
                      onClick={() => setShowQrScanner(true)}
                      className="bg-restaurant-primary hover:bg-restaurant-primary-hover px-6 py-2 h-auto w-full sm:w-auto"
                      size="lg"
                    >
                      <Camera className="w-5 h-5 mr-2" />
                      Scan QR Code
                    </Button>
                    <p className="text-sm text-muted-foreground">
                      Use this as a backup if the customer scan page isn't working
                    </p>
                    <div className="mt-4 w-full max-w-md">
                      <p className="text-xs text-muted-foreground mb-2 text-left">Or enter customer ID manually:</p>
                      <Input 
                        type="text"
                        placeholder="Enter customer ID"
                        onChange={(e) => setActiveCustomerId(e.target.value)}
                        className="border-restaurant-border focus-visible:ring-restaurant-primary"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-medium">Scan Customer QR Code</h3>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setShowQrScanner(false)}
                        className="border-restaurant-primary text-restaurant-primary hover:bg-restaurant-primary/10"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div id="staff-qr-scanner" className="qr-scanner-container max-w-full overflow-hidden"></div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Customer Profile Modal */}
      <Dialog open={showScanModal} onOpenChange={handleModalOpenChange}>
        <DialogContent className="max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <User className="w-5 h-5 mr-2 text-restaurant-primary" />
              {activeCustomerName ? `New Customer Scan: ${activeCustomerName}` : "Customer Profile"}
            </DialogTitle>
            <DialogDescription>
              Customer just scanned their QR code
            </DialogDescription>
          </DialogHeader>
          {activeCustomerId && (
            <CustomerDetails
              customerId={activeCustomerId}
              onClose={() => handleModalOpenChange(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Regular Customer Details Section */}
      {activeCustomerId && !showScanModal && (
        <Card className="border-t-4 border-t-restaurant-primary shadow-lg">
          <CardContent className="p-4 sm:p-6">
            <CustomerDetails
              customerId={activeCustomerId}
              onClose={() => setActiveCustomerId("")}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StaffDashboard;
